import React, { createContext, useContext, useEffect, useState } from 'react'
import useFetch from '../hooks/useFetch';
import { getRelevantGroupOffers, getReservationCountSummary } from '../config/apiRoutes';
import { format } from 'date-fns';
import AuthContext from './AuthContext';
import { ReservationCountSummary } from '../models/Reservation';

type Props = {
    children: any;
}

interface NavBadgeContextState {
    data: Record<string, number>;
    updateKeyValue: (key: string, value: number) => void;
    getValue: (key: string) => number;
    hasValue: (key: string) => boolean;
    refreshData: (type?: 'group_reservation' | 'waitlist' | "reservations") => void;
    autoRefresh: boolean;
    setAutoRefresh: (value: boolean) => void;
    countSummary: ReservationCountSummary;
}

const NavBadgeContext = createContext<NavBadgeContextState>(null as any);

export const NavBadgeProvider = (props: Props) => {
    const { userInfo } = useContext(AuthContext);
    const caller = useFetch();
    //keys matched with pageRoute titles
    const [data, setData] = useState<Record<string, number>>({});
    const [countSummary, setCountSummary] = useState<ReservationCountSummary>({ no_table: 0, unapproved: 0, waitlist: 0, today_deletes: 0, arrived: 0 });
    const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

    useEffect(() => {
        if (!userInfo) return;
        fetchActiveGroupReservations();
        fetchCountSummary();
    }, [userInfo]);

    useEffect(() => {
        if (!autoRefresh) return;

        const interval = setInterval(() => {
            refreshData();
        }, 5 * 60 * 1000); // 5 minutes

        return () => clearInterval(interval);
    }, [autoRefresh]);

    async function fetchActiveGroupReservations() {
        if (userInfo?.selected_restaurant.group_reservations) {
            const url = `${getRelevantGroupOffers}/count?date=${format(new Date(), 'yyyy-MM-dd')}`;
            const result = await caller.call<undefined, { active_total: number }>('GET', url)
            if (result.ok && result.data) {
                const groupReservationCount = result.data.active_total;
                setData((prevData) => ({
                    ...prevData,
                    'group_reservation': groupReservationCount,
                }));
            }
        }
        else {
            setData((prevData) => ({
                ...prevData,
                'group_reservation': 0,
            }));
        }
    }

    /* async function fetchActiveWaitlist() {
       if (userInfo?.selected_restaurant.waitlist) {
            const url = `${getActiveWaitlist}/count?date=${format(new Date(), 'yyyy-MM-dd')}`;
            const result = await caller.call<undefined, { active_total: number }>('GET', url)
            if (result.ok && result.data) {
                const waitlistCount = result.data.active_total;
                setData((prevData) => ({
                    ...prevData,
                    'waitlist': waitlistCount,
                }));
            }
        }
        else {
            setData((prevData) => ({
                ...prevData,
                'waitlist': 0,
            }));
        }
    }*/

    async function fetchCountSummary() {
        const response = await caller.call<undefined, ReservationCountSummary>("GET", getReservationCountSummary);
        if (response.ok && response.data) {
            setCountSummary(response.data);
            setData((prevData) => ({
                ...prevData,
                reservations: response.data!.unapproved,
                waitlist: response.data!.waitlist,
            }));
        }
    }


    const updateKeyValue = (key: string, value: number) => {
        setData((prevData) => ({
            ...prevData,
            [key]: value,
        }));
    };

    const getValue = (key: string): number => data[key] || 0;

    const hasValue = (key: string): boolean => getValue(key) > 0;

    const refreshData = async (type?: 'group_reservation' | 'waitlist' | 'reservations') => {
        if (type === 'group_reservation') {
            await fetchActiveGroupReservations();
        } else if (type === 'waitlist') {
            await fetchCountSummary();
        } else if (type === "reservations") {
            await fetchCountSummary();
        } else {
            await fetchActiveGroupReservations();
            await fetchCountSummary();
        }
    };

    const state = {
        data,
        updateKeyValue,
        getValue,
        hasValue,
        refreshData,
        autoRefresh,
        setAutoRefresh,
        countSummary,
    }

    return (
        <NavBadgeContext.Provider value={state} >
            {props.children}
        </NavBadgeContext.Provider>
    )
}

export default NavBadgeContext